// src/components/EnrollmentModal/EnrollmentModal.tsx
import React, { useMemo, useRef, useState } from "react";
import "./EnrollmentModal.css";

const API_BASE =
  (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");

type Row = {
  record_type: string;
  student_id?: string;
  student_first?: string;
  student_last?: string;
  student_grade?: string;
  pt_role?: string;
  pt_name?: string;
  pt_email?: string;
  reference?: string;
};

type SchoolProfile = {
  org_id?: number;
  org_code?: string;
  school_name?: string;
  admin_name?: string;
  admin_email?: string;
  admin_phone?: string;
  city?: string;
  state?: string;
  district?: string;
};

type Props = {
  orgId?: number;
  token: string;
  profile?: SchoolProfile | null;
  onClose: () => void;
  onSubmitted?: () => void;
  // Compatibility with the older School Admin dashboard prop name.
  onSuccess?: () => void;
};

function csvEscape(value: string): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
      continue;
    }

    current += ch;
  }

  out.push(current.trim());
  return out;
}

async function downloadBlobAsFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function EnrollmentModal({
  orgId,
  token,
  profile,
  onClose,
  onSubmitted,
  onSuccess,
}: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  const resolvedOrgId = Number(orgId || profile?.org_id || 0);
  const schoolName = profile?.school_name || "—";
  const orgCode = profile?.org_code || "—";
  const adminName = profile?.admin_name || "—";
  const adminEmail = profile?.admin_email || "—";
  const adminPhone = profile?.admin_phone || "—";

  const rowCountLabel = useMemo(() => `${rows.length} row${rows.length === 1 ? "" : "s"}`, [rows.length]);

  const addStudent = () => {
    setRows((prev) => [...prev, { record_type: "student" }]);
  };

  const addPT = () => {
    setRows((prev) => [...prev, { record_type: "pt" }]);
  };

  const deleteRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof Row, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleRecordTypeChange = (index: number, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      const current = next[index];

      if (value === "student") {
        next[index] = {
          record_type: "student",
          student_id: current.student_id || "",
          student_first: current.student_first || "",
          student_last: current.student_last || "",
          student_grade: current.student_grade || "",
          pt_role: "",
          pt_name: "",
          pt_email: "",
          reference: "",
        };
      } else {
        next[index] = {
          record_type: "pt",
          student_id: "",
          student_first: "",
          student_last: "",
          student_grade: "",
          pt_role: current.pt_role || "",
          pt_name: current.pt_name || "",
          pt_email: current.pt_email || "",
          reference: current.reference || "",
        };
      }

      return next;
    });
  };

  const downloadTemplate = async () => {
    try {
      const headers = [
        "record_type",
        "student_id",
        "student_first",
        "student_last",
        "student_grade",
        "pt_role",
        "pt_name",
        "pt_email",
        "reference",
      ];

      const sampleRows = [
        ["student", "94414", "Margaret", "Smith", "8", "", "", "", ""],
        ["student", "41449", "Jacob", "Perry", "8", "", "", "", ""],
        ["pt", "", "", "", "", "parent", "Mary Smith", "me@example.com", "Margaret Smith"],
        ["pt", "", "", "", "", "teacher", "Bill Watson", "middle@ekidmiddle.com", "Grade 8 / Homeroom"],
      ];

      const csvText = [
        headers.map(csvEscape).join(","),
        ...sampleRows.map((row) => row.map(csvEscape).join(",")),
      ].join("\r\n");

      await downloadBlobAsFile(
        new Blob([csvText], { type: "text/csv;charset=utf-8;" }),
        "enrollment_template.csv"
      );

      setMsg("Enrollment CSV template download started.");
    } catch {
      setMsg("Template download failed.");
    }
  };

  const importCSV = async (file?: File) => {
    if (!file) return;

    try {
      const text = await file.text();
      // Accept Windows CRLF, Unix LF, and legacy/Excel CR-only CSV line endings.
      // Some spreadsheet exports use bare carriage returns; splitting only on
      // \\r?\\n makes the entire CSV look like a single line and triggers the
      // misleading "CSV file is empty" message.
      const lines = text.split(/\r\n|\n|\r/).filter((line) => line.trim());

      if (lines.length <= 1) {
        setMsg("CSV file is empty.");
        return;
      }

      const header = parseCsvLine(lines[0]).map((value) =>
        value.replace(/^\uFEFF/, "").trim().toLowerCase()
      );
      const expectedHeader = [
        "record_type",
        "student_id",
        "student_first",
        "student_last",
        "student_grade",
        "pt_role",
        "pt_name",
        "pt_email",
        "reference",
      ];

      if (expectedHeader.some((name, index) => header[index] !== name)) {
        setMsg("CSV columns do not match the Enrollment template. Please download a fresh template and try again.");
        return;
      }

      const parsed: Row[] = [];

      for (let rowNumber = 2; rowNumber <= lines.length; rowNumber += 1) {
        const c = parseCsvLine(lines[rowNumber - 1]);
        const recordType = (c[0] || "").trim().toLowerCase();

        if (recordType !== "student" && recordType !== "pt") {
          setMsg(`CSV row ${rowNumber}: record_type must be "student" or "pt".`);
          return;
        }

        parsed.push({
          record_type: recordType,
          student_id: (c[1] || "").trim(),
          student_first: (c[2] || "").trim(),
          student_last: (c[3] || "").trim(),
          student_grade: (c[4] || "").trim(),
          pt_role: (c[5] || "").trim().toLowerCase(),
          pt_name: (c[6] || "").trim(),
          pt_email: (c[7] || "").trim(),
          reference: (c[8] || "").trim(),
        });
      }

      setRows(parsed);
      setMsg(`Imported ${parsed.length} rows from CSV.`);
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "CSV import failed.";
      setMsg(message);
    } finally {
      if (csvInputRef.current) {
        csvInputRef.current.value = "";
      }
    }
  };

  const validateRows = (): string => {
    if (!rows.length) return "Add at least one row.";

    for (const r of rows) {
      if (r.record_type === "student") {
        if (!r.student_id || !r.student_first || !r.student_last || !r.student_grade) {
          return "Each Student row requires Student ID, First, Last, and Grade.";
        }
      }

      if (r.record_type === "pt") {
        if (!r.pt_role || !r.pt_name) {
          return "Each Parent / Teacher row requires Role and Name.";
        }
      }
    }

    return "";
  };

  const notifySubmitted = () => {
    if (onSubmitted) onSubmitted();
    else if (onSuccess) onSuccess();
    else onClose();
  };

  const buildRosterCsv = (): string => {
    const headers = [
      "record_type",
      "student_id",
      "student_first",
      "student_last",
      "student_grade",
      "student_pin",
      "pt_code",
      "pt_role",
      "pt_pin",
      "pt_name",
      "pt_email",
    ];

    const csvRows = rows.map((r, index) => {
      if (r.record_type === "student") {
        return [
          "student",
          r.student_id || "",
          r.student_first || "",
          r.student_last || "",
          r.student_grade || "",
          "",
          "",
          "",
          "",
          "",
          "",
        ];
      }

      const generatedCode = `PT${String(index + 1).padStart(4, "0")}`;
      return [
        "pt",
        "",
        "",
        "",
        "",
        "",
        generatedCode,
        r.pt_role || "",
        "",
        r.pt_name || "",
        r.pt_email || "",
      ];
    });

    return [headers, ...csvRows]
      .map((row) => row.map((value) => csvEscape(String(value || ""))).join(","))
      .join("\r\n");
  };

  const submitThroughRosterCompatibility = async () => {
    if (!resolvedOrgId) throw new Error("School organization ID is missing.");

    const csvBlob = new Blob([buildRosterCsv()], { type: "text/csv;charset=utf-8" });
    const formData = new FormData();
    formData.append("file", csvBlob, "school_enrollment.csv");

    const uploadResp = await fetch(
      `${API_BASE}/school/roster-submissions/upload?org_id=${encodeURIComponent(String(resolvedOrgId))}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );
    const uploadData = await uploadResp.json().catch(() => ({}));
    if (!uploadResp.ok) {
      throw new Error(uploadData?.detail || uploadData?.message || "Roster upload failed.");
    }
    if (uploadData?.has_errors) {
      const validationDetail =
        uploadData?.detail ||
        uploadData?.message ||
        (Array.isArray(uploadData?.errors)
          ? uploadData.errors.filter(Boolean).join("; ")
          : "");
      throw new Error(
        validationDetail ||
          "The roster contains validation errors. Please review the imported rows."
      );
    }

    const submissionId = Number(uploadData?.submission_id || 0);
    if (!submissionId) throw new Error("Roster submission ID was not returned.");

    const submitResp = await fetch(
      `${API_BASE}/school/roster-submissions/submit/${submissionId}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const submitData = await submitResp.json().catch(() => ({}));
    if (!submitResp.ok) {
      throw new Error(submitData?.detail || submitData?.message || "Roster submission failed.");
    }
  };

  const submit = async () => {
    const validationError = validateRows();
    if (validationError) {
      setMsg(validationError);
      return;
    }

    setBusy(true);
    setMsg("");

    try {
      if (!resolvedOrgId) {
        setMsg("School organization ID is missing. Close the window, refresh the dashboard, and try again.");
        return;
      }

      const resp = await fetch(`${API_BASE}/school/enrollment-submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          org_id: resolvedOrgId,
          rows,
        }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        // Keep the existing enrollment endpoint as the primary path. Only use
        // the already-mounted roster submission workflow as a compatibility
        // fallback when the legacy endpoint is unavailable.
        if (resp.status === 404 || resp.status === 405) {
          await submitThroughRosterCompatibility();
        } else {
          const detail = data?.detail || data?.message || "Submit failed.";
          setMsg(detail);
          return;
        }
      }

      setMsg("Enrollment submitted for Admin approval.");
      notifySubmitted();
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Network error while submitting.";
      setMsg(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="em-overlay" onClick={onClose}>
      <div className="em-modal" onClick={(e) => e.stopPropagation()}>
        <div className="em-header">
          <div>
            <h2 className="em-title">Enrollment</h2>
            <div className="em-subtitle">
              Enter Student and Parent / Teacher records, or import a CSV to prefill the form.
            </div>
          </div>
          <button className="em-close" type="button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="em-schoolcard">
          <div><b>School:</b> {schoolName}</div>
          <div><b>Org Code:</b> {orgCode}</div>
          <div><b>Admin:</b> {adminName}</div>
          <div><b>Contact:</b> {adminEmail}{adminPhone && adminPhone !== "—" ? ` • ${adminPhone}` : ""}</div>
        </div>

        {msg ? <div className="em-message">{msg}</div> : null}

        <div className="em-controls">
          <div className="em-left-controls">
            <button className="em-btn em-primary" type="button" onClick={addStudent} disabled={busy}>
              Add Student
            </button>
            <button className="em-btn em-secondary" type="button" onClick={addPT} disabled={busy}>
              Add Parent / Teacher
            </button>
            <button className="em-btn em-secondary" type="button" onClick={downloadTemplate} disabled={busy}>
              Download CSV Template
            </button>
          </div>

          <div className="em-right-controls">
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => void importCSV(e.target.files?.[0])}
              disabled={busy}
              style={{ display: "none" }}
            />
            <button
              className="em-filepick"
              type="button"
              onClick={() => csvInputRef.current?.click()}
              disabled={busy}
            >
              Import CSV
            </button>
            <span className="em-count">{rowCountLabel}</span>
          </div>
        </div>

        <div className="em-tablewrap">
          <table className="em-table">
            <thead>
              <tr>
                <th>Record Type</th>
                <th>Student ID</th>
                <th>First</th>
                <th>Last</th>
                <th>Grade</th>
                <th>Role</th>
                <th>Name</th>
                <th>Email</th>
                <th>Reference</th>
                <th>PIN</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="em-empty">
                    No rows yet. Click <b>Add Student</b>, <b>Add Parent / Teacher</b>, or import a CSV.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => {
                  const isStudent = r.record_type === "student";

                  return (
                    <tr key={i}>
                      <td>
                        <select
                          className="em-input"
                          value={r.record_type}
                          onChange={(e) => handleRecordTypeChange(i, e.target.value)}
                          disabled={busy}
                        >
                          <option value="student">Student</option>
                          <option value="pt">Parent / Teacher</option>
                        </select>
                      </td>

                      <td>
                        <input
                          className="em-input"
                          value={r.student_id || ""}
                          onChange={(e) => updateRow(i, "student_id", e.target.value)}
                          disabled={busy || !isStudent}
                        />
                      </td>

                      <td>
                        <input
                          className="em-input"
                          value={r.student_first || ""}
                          onChange={(e) => updateRow(i, "student_first", e.target.value)}
                          disabled={busy || !isStudent}
                        />
                      </td>

                      <td>
                        <input
                          className="em-input"
                          value={r.student_last || ""}
                          onChange={(e) => updateRow(i, "student_last", e.target.value)}
                          disabled={busy || !isStudent}
                        />
                      </td>

                      <td>
                        <input
                          className="em-input em-grade"
                          value={r.student_grade || ""}
                          onChange={(e) => updateRow(i, "student_grade", e.target.value)}
                          disabled={busy || !isStudent}
                        />
                      </td>

                      <td>
                        <select
                          className="em-input"
                          value={r.pt_role || ""}
                          onChange={(e) => updateRow(i, "pt_role", e.target.value)}
                          disabled={busy || isStudent}
                        >
                          <option value="">Select</option>
                          <option value="parent">Parent</option>
                          <option value="teacher">Teacher</option>
                        </select>
                      </td>

                      <td>
                        <input
                          className="em-input"
                          value={r.pt_name || ""}
                          onChange={(e) => updateRow(i, "pt_name", e.target.value)}
                          disabled={busy || isStudent}
                        />
                      </td>

                      <td>
                        <input
                          className="em-input"
                          value={r.pt_email || ""}
                          onChange={(e) => updateRow(i, "pt_email", e.target.value)}
                          disabled={busy || isStudent}
                        />
                      </td>

                      <td>
                        <input
                          className="em-input"
                          value={r.reference || ""}
                          onChange={(e) => updateRow(i, "reference", e.target.value)}
                          disabled={busy || isStudent}
                          placeholder={isStudent ? "" : "Optional"}
                        />
                      </td>

                      <td className="em-pin">To be assigned</td>

                      <td>
                        <button
                          className="em-delete"
                          type="button"
                          onClick={() => deleteRow(i)}
                          disabled={busy}
                          title="Delete row"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="em-actions">
          <button className="em-btn em-primary" type="button" onClick={submit} disabled={busy}>
            {busy ? "Submitting..." : "Submit for Admin Approval"}
          </button>
          <button className="em-btn em-secondary" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}